import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import {
  clearDocuments,
  fetchDocuments,
  selectAllDocuments,
} from '../../../slices/documents/documentsSlice';
import { selectUser } from '../../../slices/user/userSlice';
import { DOCUMENT_CATEGORIES } from '../types';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import {
  fetchBusinessProfileById,
  fetchBusinessProfileContactById,
} from '../../../slices/business-profile/businessProfileSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helpers } from '../../../common/helpers/helpers';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '../../../common/constants/routes';

const useDocuments = (categoryValue?: string, workflow?: string, profileQuery?: number) => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const taskIds = useAppSelector((state: RootState) => state.tasks.taskIds);
  const documentsCount = useAppSelector((state) => state.documents.documentsCount);
  const [currentPage, setCurrentPage] = useState(1);
  const documentItems = useAppSelector(selectAllDocuments);
  const documentContractsLoading = useAppSelector((state) => state.documentContracts.status);
  const documentUploadsLoading = useAppSelector((state) => state.documents.status);
  const employeesLoading = useAppSelector((state) => state.employees.status);
  const customersLoading = useAppSelector((state) => state.customers.status);
  const suppliersLoading = useAppSelector((state) => state.suppliers.status);
  const businessProfileLoading = useAppSelector((state) => state.businessProfile.status);
  const currentUser = useAppSelector(selectUser);
  const [params] = useSearchParams();
  const activeTabInitialValue: DOCUMENT_CATEGORIES | string = params.get('activeTab')
    ? Helpers.capitalizeFirstLetter(params.get('activeTab'))
    : null;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cardView, setCardView] = useState<boolean>(true);
  const [showWorkflowBreadcrumbs, setShowWorkflowBreadcrumbs] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<DOCUMENT_CATEGORIES>('All');

  const location = useLocation();
  const path = location.pathname;
  const profilesLocations = [
    ROUTES.EMPLOYEE_PROFILES,
    ROUTES.SUPPLIER_PROFILES,
    ROUTES.CUSTOMER_PROFILES,
    ROUTES.BUSINESS_PROFILE,
  ];
  const isProfilePage = profilesLocations.some((el) => path.includes(el));

  const getNextPage = () => {
    //dispatch(clearDocuments());
    //dispatch(clearGeneratedDocuments());
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: categoryValue
            ? categoryValue
            : activeTab === 'All'
              ? ''
              : activeTab.toLowerCase(),
          page: currentPage + 1,
          ...workflowRequest,
          ...profileQueryRequest,
        },
      })
    );
    setCurrentPage(currentPage + 1);
  };

  const getRequestedPage = (passedPage: number, category?: string) => {
    const categoryToUse = category || categoryValue || activeTab;
    //dispatch(clearDocuments());
    //dispatch(clearGeneratedDocuments());
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: categoryToUse === 'All' ? '' : categoryToUse.toLowerCase(),
          page: passedPage,
          ...workflowRequest,
          ...profileQueryRequest,
        },
      })
    );
    setCurrentPage(passedPage);
  };

  const getPrevPage = () => {
    //dispatch(clearDocuments());
    //dispatch(clearGeneratedDocuments());
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: categoryValue
            ? categoryValue
            : activeTab === 'All'
              ? ''
              : activeTab.toLowerCase(),
          page: currentPage - 1,
          ...workflowRequest,
          ...profileQueryRequest,
        },
      })
    );
    setCurrentPage(currentPage - 1);
  };

  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };

  const documents = [...documentItems].sort((a, b) => sortByDate(a, b));

  const handleTabChange = (tab: DOCUMENT_CATEGORIES) => {
    setCurrentPage(1);
    setActiveTab(tab);
  };

  const workflowRequest = useMemo(() => {
    return workflow
      ? {
          workflow: workflow !== t('labels.generalUnassigned') ? workflow : String(null),
        }
      : {};
  }, [workflow, t]);

  const profileQueryRequest = useMemo(() => {
    return profileQuery ? { profile: profileQuery } : {};
  }, [profileQuery]);

  const getDocumentsDataWorkflowVersion = useCallback(() => {
    if (
      currentUser.id &&
      (workflow || (!workflow && !cardView)) &&
      taskIds &&
      taskIds.length === 0 &&
      ((showWorkflowBreadcrumbs && cardView) || !cardView) &&
      /*(profileQuery || categoryValue === 'business')*/

      isProfilePage
    ) {
      dispatch(clearDocuments());
      dispatch(
        fetchDocuments({
          data: {
            user: currentUser.id,
            category: categoryValue.toLowerCase(),
            ...workflowRequest,
            ...profileQueryRequest,
          },
        })
      );
    }
  }, [
    currentUser.id,
    workflow,
    cardView,
    taskIds,
    showWorkflowBreadcrumbs,
    isProfilePage,
    dispatch,
    categoryValue,
    workflowRequest,
    profileQueryRequest,
  ]);

  const getDocumentsDataSimpleVersion = useCallback(() => {
    if (
      currentUser.id &&
      !isProfilePage &&
      !(activeTab === 'All' && cardView) &&
      taskIds &&
      taskIds.length === 0
    ) {
      dispatch(clearDocuments());
      dispatch(
        fetchDocuments({
          data: {
            user: currentUser.id,
            category: activeTabInitialValue
              ? activeTabInitialValue.toLowerCase()
              : activeTab === 'All'
                ? ''
                : activeTab.toLowerCase(),
            ...workflowRequest,
          },
        })
      );
      // dispatch(
      //   fetchDocumentContracts({
      //     data: {
      //       user: currentUser.id,
      //       category: activeTabInitialValue
      //         ? activeTabInitialValue.toLowerCase()
      //         : activeTab === 'All'
      //         ? ''
      //         : activeTab.toLowerCase(),
      //     },
      //   })
      // );
    }
  }, [
    currentUser.id,
    isProfilePage,
    activeTab,
    cardView,
    taskIds,
    dispatch,
    activeTabInitialValue,
    workflowRequest,
  ]);

  const getEmployees = useCallback(() => {
    if (company) {
      if (employeesLoading === 'idle') {
        dispatch(fetchEmployees({ company_id: company.id }));
      }
    }
  }, [employeesLoading, company, dispatch]);

  const getCustomers = useCallback(() => {
    if (company) {
      if (customersLoading === 'idle') {
        dispatch(fetchCustomers({ company_id: company.id, page_size: 100 }));
      }
    }
  }, [customersLoading, company, dispatch]);

  const getSuppliers = useCallback(() => {
    if (company) {
      if (suppliersLoading === 'idle') {
        dispatch(fetchSuppliers({ company_id: company.id, page_size: 100 }));
      }
    }
  }, [suppliersLoading, company, dispatch]);

  const getBusinessContacts = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileContactById({ id: company.id }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  const getBusinessDetails = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileById({ id: company.id }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearDocuments());
    };
  }, [dispatch]);

  useEffect(() => {
    getEmployees();
  }, [getEmployees]);

  useEffect(() => {
    getSuppliers();
  }, [getSuppliers]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  useEffect(() => {
    getBusinessContacts();
  }, [getBusinessContacts]);

  useEffect(() => {
    getBusinessDetails();
  }, [getBusinessDetails]);

  useEffect(() => {
    getDocumentsDataWorkflowVersion();
  }, [getDocumentsDataWorkflowVersion]);

  useEffect(() => {
    getDocumentsDataSimpleVersion();
  }, [getDocumentsDataSimpleVersion]);

  useEffect(() => {
    if (activeTabInitialValue) {
      setActiveTab(Helpers.capitalizeFirstLetter(activeTabInitialValue) as DOCUMENT_CATEGORIES);
      navigate(window.location.pathname);
    }
  }, [activeTabInitialValue, navigate]);

  useEffect(() => {
    if (isProfilePage && !workflow && !showWorkflowBreadcrumbs) {
      setCurrentPage(1);
    }
  }, [isProfilePage, workflow, showWorkflowBreadcrumbs]);

  return {
    documents,
    documentContractsLoading,
    documentUploadsLoading,
    employeesLoading,
    suppliersLoading,
    customersLoading,
    businessProfileLoading,
    activeTab,
    documentsCount,
    getNextPage,
    getPrevPage,
    getRequestedPage,
    handleTabChange,
    currentPage,
    setCurrentPage,
    cardView,
    setCardView,
    showWorkflowBreadcrumbs,
    setShowWorkflowBreadcrumbs,
  };
};

export { useDocuments };
